import styled from '@emotion/styled';
import { Maybe } from 'graphql/jsutils/Maybe';
import { css, SerializedStylesFn, ThemeProps } from '@emotion/react';

import { Wws_Page, Wws_PageItem, Wws_PhotoAlbum } from '../../types/graphql';
import { themeFont } from '../../lib/themeFont';
import PageContents, { ComponentOverrideMapValue } from '../PageContents';
import EventGroupItem from '../PageItems/EventGroupItem';
import { getSectionId } from '../Templates/SinglePageTemplate';
import RSVPButton from '../RSVPButton';
import SinglePageRegistrySection from './Sections/RegistrySection/SinglePageRegistrySection';
import InlineEditHandler from '../InlineEdit/InlineEditHandler/InlineEditHandler';
import { buildEditPageTitleContextMenuButtons } from '../InlineEdit/InlineEditMessageFactory';

const MobileSectionTitle = ({
  hasSeparator,
  theme,
}: ThemeProps & SectionTitleProps) => css`
  border-top:
    ${
      hasSeparator
        ? `1px solid ${theme.components.DateLocationLockup?.borderColor || theme.fonts.s4.color}`
        : 'none'
    }}
`;

interface SectionTitleProps {
  hasSeparator?: boolean;
  isMobile?: boolean;
}
const SectionTitle = styled('h1')`
  ${themeFont('s2')}

  padding-top: 60px;
  text-align: center;
  margin-top: 120px;
  margin-bottom: 64px;

  ${({
    isMobile,
  }: SectionTitleProps): SerializedStylesFn<SectionTitleProps & ThemeProps> =>
    isMobile ? MobileSectionTitle : undefined}
`;

const PageSection = styled('section')`
  padding: 0 16px;
`;

const EventGroupItemWrapper = styled('div')`
  text-align: center;
  @media (max-width: 1024px) {
    text-align: initial;
  }
`;

const renderSectionContent = (
  page: Wws_Page,
  isMobile: boolean,
  photoAlbums?: Maybe<Wws_PhotoAlbum[]>
) => {
  const isRegistry = page.type === 'RegistryPage';
  const isRSVPPage = page.type === 'RsvpNavItem';

  const componentOverrides = new Map<string, ComponentOverrideMapValue>();
  componentOverrides.set(
    'WWS_EventGroupItem',
    ({ item }: { item: Wws_PageItem }) => (
      <EventGroupItemWrapper>
        <EventGroupItem page={page} item={item} />
      </EventGroupItemWrapper>
    )
  );

  if (isRegistry) {
    return <SinglePageRegistrySection isMobile={isMobile} />;
  }

  if (isRSVPPage) {
    return <RSVPButton />;
  }

  return (
    <PageContents
      page={page}
      photoAlbums={photoAlbums}
      componentOverrides={componentOverrides}
    />
  );
};

export interface SinglePageSectionProps {
  page: Wws_Page;
  isMobile: boolean;
  isFirstPage: boolean;
  shouldShowTitle?: boolean;
  photoAlbums?: Maybe<Wws_PhotoAlbum[]>;
}

const SinglePageSection = ({
  page,
  isMobile,
  isFirstPage,
  shouldShowTitle = true,
  photoAlbums,
}: SinglePageSectionProps) => {
  const sectionId = getSectionId(page);

  return (
    <PageSection
      key={page.id}
      id={sectionId}
      data-testid="SinglePageSection__PageSection"
    >
      {shouldShowTitle && (
        <SectionTitle
          hasSeparator={!isFirstPage}
          isMobile={isMobile}
          data-testid="SinglePageSection__SectionTitle"
        >
          <InlineEditHandler
            contextMenuButtons={buildEditPageTitleContextMenuButtons(
              page as Wws_Page
            )}
            editModeOverride="forceHide" // Hiding the handler until ios has an event interceptor for editing page titles built
          >
            {page.title}
          </InlineEditHandler>
        </SectionTitle>
      )}
      {renderSectionContent(page, isMobile, photoAlbums)}
    </PageSection>
  );
};

export default SinglePageSection;
